import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { site } from "../../lib/site";
import { IconArrow, IconCheck, IconPhone, IconShield } from "../Icon";
import { Counter } from "../motion/Counter";
import { Magnetic } from "../motion/Magnetic";

const ease = [0.22, 1, 0.36, 1] as const;

const container = {
  hidden: {},
  show: { transition: { staggerChildren: 0.08, delayChildren: 0.12 } },
};
const fadeUp = {
  hidden: { opacity: 0, y: 26, filter: "blur(6px)" },
  show: { opacity: 1, y: 0, filter: "blur(0px)", transition: { duration: 0.6, ease } },
};

export function Hero() {
  const { scrollY } = useScroll();
  const blob1Y = useTransform(scrollY, [0, 600], [0, 120]);
  const blob2Y = useTransform(scrollY, [0, 600], [0, -80]);
  const gridY = useTransform(scrollY, [0, 600], [0, 60]);

  return (
    <section className="relative overflow-hidden isolate">
      <div className="absolute inset-0 -z-10 gradient-hero" aria-hidden="true" />

      <motion.div
        aria-hidden="true"
        className="absolute inset-0 -z-10 opacity-[0.45]"
        style={{ y: gridY }}
      >
        <svg className="h-full w-full" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <pattern id="hero-grid" width="56" height="56" patternUnits="userSpaceOnUse">
              <path d="M 56 0 L 0 0 0 56" fill="none" stroke="#A5F3FC" strokeWidth="0.8" />
            </pattern>
            <radialGradient id="grid-mask" cx="50%" cy="30%" r="60%">
              <stop offset="0" stopColor="white" stopOpacity="1" />
              <stop offset="1" stopColor="white" stopOpacity="0" />
            </radialGradient>
            <mask id="hero-grid-mask">
              <rect width="100%" height="100%" fill="url(#grid-mask)" />
            </mask>
          </defs>
          <rect width="100%" height="100%" fill="url(#hero-grid)" mask="url(#hero-grid-mask)" />
        </svg>
      </motion.div>

      <motion.div
        aria-hidden="true"
        style={{ y: blob1Y }}
        className="absolute -top-24 -right-24 h-[420px] w-[420px] rounded-full bg-gradient-to-br from-brand-300/50 via-brand-400/30 to-transparent blur-3xl -z-10"
      />
      <motion.div
        aria-hidden="true"
        style={{ y: blob2Y }}
        className="absolute -bottom-32 -left-24 h-[380px] w-[380px] rounded-full bg-gradient-to-tr from-accent-400/40 via-brand-300/30 to-transparent blur-3xl -z-10"
      />

      <div className="container-page pt-10 sm:pt-16 lg:pt-24 pb-20 lg:pb-28 grid lg:grid-cols-12 gap-10 lg:gap-14 items-center">
        <motion.div
          className="lg:col-span-7"
          variants={container}
          initial="hidden"
          animate="show"
        >
          <motion.span variants={fadeUp} className="eyebrow">
            <span className="relative flex h-1.5 w-1.5">
              <span className="absolute inset-0 rounded-full bg-accent-500 animate-ping opacity-60" />
              <span className="relative h-1.5 w-1.5 rounded-full bg-accent-600" />
            </span>
            Agent d'entretien à Vichy (03)
          </motion.span>

          <motion.h1 variants={fadeUp} className="mt-5 text-balance">
            Nettoyage{" "}
            <span className="relative inline-block">
              <span className="bg-gradient-to-r from-brand-600 via-brand-500 to-accent-600 bg-clip-text text-transparent">
                professionnel
              </span>
              <motion.svg
                aria-hidden="true"
                className="absolute left-0 -bottom-2 w-full"
                viewBox="0 0 300 14"
                preserveAspectRatio="none"
                initial={{ pathLength: 0, opacity: 0 }}
                animate={{ pathLength: 1, opacity: 1 }}
                transition={{ duration: 1.1, delay: 0.7, ease }}
              >
                <motion.path
                  d="M2 8 Q 80 2 150 7 T 298 6"
                  fill="none"
                  stroke="#22D3EE"
                  strokeWidth="3"
                  strokeLinecap="round"
                />
              </motion.svg>
            </span>{" "}
            à Vichy et dans l'Allier
          </motion.h1>

          <motion.p variants={fadeUp} className="mt-6 text-lg text-ink-muted max-w-2xl">
            Bureaux, copropriétés, commerces, vitres, fin de chantier et entretien chez
            particuliers. Un interlocuteur unique, local et assuré, qui livre un résultat impeccable -
            devis gratuit sous 48 h.
          </motion.p>

          <motion.ul
            variants={fadeUp}
            className="mt-7 grid sm:grid-cols-2 gap-2.5 max-w-xl"
          >
            {[
              "Interlocuteur unique & assuré",
              "Produits écologiques disponibles",
              "Carnet de passage inclus",
              "Intervention 7j/7",
            ].map((t) => (
              <li key={t} className="flex items-center gap-2.5 text-ink">
                <span className="grid h-7 w-7 place-items-center rounded-full bg-accent-500/15 text-accent-700 ring-1 ring-accent-500/30">
                  <IconCheck className="h-3.5 w-3.5" strokeWidth={2.5} />
                </span>
                <span className="text-sm font-medium">{t}</span>
              </li>
            ))}
          </motion.ul>

          <motion.div variants={fadeUp} className="mt-9 flex flex-wrap gap-3">
            <Magnetic>
              <Link to="/contact" className="btn-primary group">
                Obtenir mon devis gratuit
                <IconArrow className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
              </Link>
            </Magnetic>
            <a href={site.phoneHref} className="btn-ghost">
              <IconPhone className="h-4 w-4" />
              {site.phoneDisplay}
            </a>
          </motion.div>

          <motion.div
            variants={fadeUp}
            className="mt-10 flex flex-wrap items-center gap-x-6 gap-y-3"
          >
            <div className="flex items-center gap-2.5">
              <span className="grid h-10 w-10 place-items-center rounded-full bg-brand-50 text-brand-700 ring-1 ring-brand-100">
                <IconShield className="h-5 w-5" />
              </span>
              <div className="text-sm">
                <div className="font-semibold text-brand-900">Entreprise locale</div>
                <div className="text-ink-muted">Depuis {site.foundingYear} · Vichy (03)</div>
              </div>
            </div>
          </motion.div>
        </motion.div>

        <motion.div
          className="lg:col-span-5"
          initial={{ opacity: 0, y: 40, scale: 0.97 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 0.8, delay: 0.25, ease }}
        >
          <HeroCard />
        </motion.div>
      </div>

      <svg
        aria-hidden="true"
        className="block w-full"
        viewBox="0 0 1440 60"
        preserveAspectRatio="none"
      >
        <path d="M0,30 C360,60 1080,0 1440,30 L1440,60 L0,60 Z" fill="#ffffff" />
      </svg>
    </section>
  );
}

function HeroCard() {
  return (
    <div className="relative">
      <motion.div
        aria-hidden="true"
        className="absolute -inset-6 bg-gradient-to-br from-brand-300/40 via-transparent to-accent-500/30 rounded-[2rem] blur-2xl"
        animate={{ opacity: [0.6, 1, 0.6] }}
        transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
      />

      <div className="relative rounded-3xl bg-white/80 backdrop-blur-xl border border-white ring-1 ring-brand-100 shadow-soft-lg overflow-hidden">
        <div className="relative p-6 bg-gradient-to-br from-brand-700 via-brand-600 to-brand-800 text-white overflow-hidden">
          <div
            className="absolute inset-0 opacity-20"
            aria-hidden="true"
            style={{
              backgroundImage:
                "radial-gradient(circle at 20% 10%, rgba(255,255,255,0.4), transparent 35%), radial-gradient(circle at 80% 90%, rgba(34,211,238,0.5), transparent 40%)",
            }}
          />
          <div className="relative">
            <div className="text-xs uppercase tracking-widest text-brand-100">
              Engagement qualité
            </div>
            <div className="mt-1 font-display font-bold text-2xl">Propreté garantie à 100 %</div>
            <p className="mt-2 text-sm text-brand-100/90">
              Si un point n'est pas conforme, nous revenons gratuitement sous 24 h.
            </p>
          </div>
        </div>

        <div className="p-5 grid gap-3">
          <Stat prefix="" valueNum={100} suffix=" %" label="Satisfait ou refait" />
          <Stat prefix="" valueNum={30} suffix=" km" label="Rayon depuis Vichy" />
          <Stat prefix="" valueNum={48} suffix=" h" label="Délai moyen pour un devis" />
        </div>
      </div>
    </div>
  );
}

function Stat({
  prefix,
  valueNum,
  suffix,
  label,
}: {
  prefix?: string;
  valueNum: number;
  suffix?: string;
  label: string;
}) {
  return (
    <motion.div
      whileHover={{ x: 2 }}
      transition={{ type: "spring", stiffness: 300, damping: 20 }}
      className="flex items-center justify-between rounded-2xl bg-gradient-to-r from-surface-soft to-white px-4 py-3 border border-slate-200/70"
    >
      <span className="font-display font-bold text-xl text-brand-800">
        <Counter to={valueNum} prefix={prefix} suffix={suffix} />
      </span>
      <span className="text-sm text-ink-muted text-right">{label}</span>
    </motion.div>
  );
}
